import type { ResultOf } from "@graphql-typed-document-node/core"
import { graphql } from "@/gql"

// Fetch all backlinks (records linking to this record)
export const fetchBacklinks = graphql(`
  query fetchRecordBacklinks($linked_id: Int!) {
    record_links(where: { linked_id: { _eq: $linked_id } }, order_by: { id: asc }) {
      ...RecordLinkFields
      record {
        ...RequiredRecordFields
        description
        data
        schema_id
      }
    }
  }
`)

/** One backlink: the link row and the record pointing here. */
export type BacklinkRow = ResultOf<typeof fetchBacklinks>["record_links"][number]

// Fetch a batch of links by id (for resolving link ids from history changes)
export const fetchLinksByIds = graphql(`
  query fetchLinksByIds($ids: [Int!]!) {
    record_links(where: { id: { _in: $ids } }) {
      ...RecordLinkFields
    }
  }
`)

// MERGE OPERATIONS ============================================================

// TIMELINE QUERIES ============================================================

// ADMIN QUERIES ================================================================

// Fetch all links for a specific schema and field key
export const fetchLinksForField = graphql(`
  query fetchLinksForField($schema_id: Int!, $schema_key: String!) {
    record_links(
      where: { schema_id: { _eq: $schema_id }, schema_key: { _eq: $schema_key } }
      order_by: { created_at: desc }
    ) {
      id
      record_id
      linked_id
      schema_id
      schema_key
      actor_person_id
      created_at
      updated_at
      record {
        ...RequiredRecordFields
      }
      linked {
        ...RequiredRecordFields
      }
    }
  }
`)

// Fetch link statistics for a schema and field key
export const fetchLinkStats = graphql(`
  query fetchLinkStats($schema_id: Int!, $schema_key: String!) {
    stats: record_links_aggregate(
      where: { schema_id: { _eq: $schema_id }, schema_key: { _eq: $schema_key } }
    ) {
      aggregate {
        count
        sources: count(columns: record_id, distinct: true)
        targets: count(columns: linked_id, distinct: true)
      }
    }
  }
`)

// LINK FIELD FILTERS ==========================================================

// The targets of a filtered link field that its filter no longer offers: the
// chips that show a warning.
export const fetchLinksOutsideFilter = graphql(`
  query fetchLinksOutsideFilter(
    $sourceType: String!
    $fieldKey: String!
    $targetType: String!
    $filter: jsonb!
  ) {
    links: links_outside_filter(
      args: {
        source_type: $sourceType
        field_key: $fieldKey
        target_type: $targetType
        filter: $filter
      }
      distinct_on: linked_id
      order_by: { linked_id: asc }
    ) {
      id
      linked_id
    }
  }
`)

// A link field's filter as the schema editor checks it: how many records it
// offers, and the existing links it would no longer offer.
export const checkLinkFieldFilter = graphql(`
  query checkLinkFieldFilter(
    $sourceType: String!
    $fieldKey: String!
    $targetType: String!
    $filter: jsonb!
  ) {
    matches: filter_records_aggregate(args: { record_type: $targetType, filter: $filter }) {
      aggregate {
        count
      }
    }
    outside: links_outside_filter_aggregate(
      args: {
        source_type: $sourceType
        field_key: $fieldKey
        target_type: $targetType
        filter: $filter
      }
    ) {
      aggregate {
        count
      }
    }
    examples: links_outside_filter(
      args: {
        source_type: $sourceType
        field_key: $fieldKey
        target_type: $targetType
        filter: $filter
      }
      limit: 5
    ) {
      id
      record {
        ...RequiredRecordFields
      }
      linked {
        ...RequiredRecordFields
      }
    }
  }
`)
